import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { T, t } from '@sm/intl';
import { Box } from '@wds/box';
import { Input } from '@wds/input';
import { Menu } from '@wds/menu';
import { Typography } from '@wds/typography';
import ThemeRow from './ThemeRow';
import AddThemeButton from './AddButton';
import LinkStyleButton from '../../../shared/LinkStyleButton';
import { validateTheme, NO_ERRORS } from '../../../validators';
import { filterCheckedThemesBySearch } from '../../../lib';
export const COPY = {
  addThemeButton: {
    id: 'OpenEndedCoding.ThemeMenu.addThemeButton',
    defaultMessage: 'Add theme',
    description: '[Type: Header][Vis: med] - button text theme selection'
  },
  title: {
    id: 'OpenEndedCoding.ThemeMenu.title',
    defaultMessage: 'Add theme',
    description: '[Type: Header][Vis: med] - Add theme title'
  },
  label: {
    id: 'OpenEndedCoding.ThemeMenu.label',
    defaultMessage: 'Add theme',
    description: '[Type: Header][Vis: med] - Search theme label'
  },
  placeholder: {
    id: 'OpenEndedCoding.ThemeMenu.placeholder',
    defaultMessage: 'Type to search or add new',
    description: '[Type: Header][Vis: med] - Search theme placeholder'
  },
  placeholderNoThemes: {
    id: 'OpenEndedCoding.ThemeMenu.placeholderNoThemes',
    defaultMessage: 'Add a theme',
    description: '[Type: Header][Vis: med] - Search theme placeholder'
  }
};
const useStyles = createUseStyles(theme => ({
  container: {
    display: 'flex',
    alignItems: 'center',
    // How we apply custom styles to the menu.
    '& div[role=menu] > div': {
      minWidth: '280px',
      maxHeight: 'none',
      // Ensure whole menu doesn't scroll vertically.
      overflowY: 'visible',
      // Make theme section scroll vertically.
      '& > ul:nth-of-type(2) > section': {
        height: '200px',
        overflowY: 'auto'
      },
      // Need extra padding for the menu items.
      '& div[role=menuitem]': {
        paddingLeft: theme.spacing[3],
        paddingRight: theme.spacing[3]
      }
    }
  },
  error: {
    overflowWrap: 'break-word',
    whiteSpace: 'normal'
  }
}));
const isEmptyState = ({
  allThemes,
  themesAvailableForSelection
}) => {
  const hasThemes = allThemes.length > 0;
  const hasThemesAvailableForSelection = themesAvailableForSelection.length > 0;
  // When there are themes check if no remaining themes to select
  if (hasThemes) {
    return !hasThemesAvailableForSelection;
  }
  // There are no themes created
  return true;
};
const ThemeMenu = ({
  responseTextId,
  allThemes,
  themesAvailableForSelection,
  addThemesToResponse,
  createThemeForResponse
}) => {
  const classes = useStyles();
  const [showThemeMenu, setShowThemeMenu] = useState(false);
  const [error, setError] = useState(NO_ERRORS);
  const [search, setSearch] = useState('');
  const [searchDisabled, setSearchDisabled] = useState(false);
  const [themesWithChecked, setThemesWithChecked] = useState(themesAvailableForSelection.map(theme => {
    return Object.assign(Object.assign({}, theme), {
      checked: false,
      show: true
    });
  }));
  // When the themes get updated, we need to refresh the theme menu.
  useEffect(() => {
    setThemesWithChecked(themesAvailableForSelection.map(theme => {
      return Object.assign(Object.assign({}, theme), {
        checked: false,
        show: true
      });
    }));
  }, [themesAvailableForSelection]);
  const themesSelected = themesWithChecked.filter(theme => theme.checked);
  const themesToShow = themesWithChecked.filter(theme => theme.show);
  const isEmpty = isEmptyState({
    allThemes,
    themesAvailableForSelection
  });
  // Need to update when theme checked
  const handleThemeChange = ({
    themeId,
    checked
  }) => {
    setThemesWithChecked(prevThemes => prevThemes.map(theme => themeId === theme.id ? Object.assign(Object.assign({}, theme), {
      checked
    }) : theme));
  };
  // Trigger addition of themes to a response
  const handleSaveThemes = () => {
    addThemesToResponse(themesSelected.map(theme => theme.id));
    setShowThemeMenu(false);
    setSearch('');
    setSearchDisabled(false);
  };
  // Trigger theme creation and add to response
  const handleThemeCreationForResponse = () => {
    const validation = validateTheme({
      themes: allThemes,
      themeName: search
    });
    setError(validation);
    if (!validation.error) {
      createThemeForResponse(search);
      setShowThemeMenu(false);
      setSearch('');
      setSearchDisabled(false);
    }
  };
  // Search for themes
  const handleSearch = e => {
    const searchPhrase = e.currentTarget.value;
    setSearch(searchPhrase);
    setError(NO_ERRORS);
    setThemesWithChecked(filterCheckedThemesBySearch({
      searchPhrase,
      themes: themesWithChecked
    }));
  };
  const handleSearchDisable = (themes, themeId, currentElementChecked) => {
    setSearchDisabled(currentElementChecked || themes.filter(theme => theme.id !== themeId).some(theme => theme.checked));
  };
  return _jsx(Box, {
    className: classes.container,
    px: 3,
    children: _jsxs(Menu, {
      placement: "bottom-left",
      show: showThemeMenu,
      onChange: show => setShowThemeMenu(show),
      menuTrigger: _jsx(LinkStyleButton, {
        children: _jsx(T, {
          desc: COPY.addThemeButton
        })
      }),
      children: [_jsx(Menu.Group, {
        children: _jsx(Box, {
          py: 4,
          px: 3,
          children: _jsx(Input, Object.assign({
            ariaLabel: t(COPY.label),
            label: t(COPY.label),
            value: search,
            disabled: searchDisabled,
            placeholder: isEmpty ? t(COPY.placeholderNoThemes) : t(COPY.placeholder),
            onChange: handleSearch
          }, error.error && {
            color: 'warning',
            errorMessage: _jsx(Box, {
              className: classes.error,
              children: error.messages.map(message => _jsx(Typography, {
                component: "p",
                variant: "body",
                color: "warning",
                children: message
              }, message))
            })
          }))
        })
      }), _jsx(Menu.Group, {
        children: _jsx(_Fragment, {
          children: themesToShow.map(theme => _jsx(Menu.Item, {
            onClick: e => {
              var _a, _b, _c;
              // when clicking on the checkbox label, a double event was firing
              const parentId = (_c = (_b = (_a = e.target) === null || _a === void 0 ? void 0 : _a.parentElement) === null || _b === void 0 ? void 0 : _b.id) !== null && _c !== void 0 ? _c : '';
              if (parentId.startsWith('ThemeRow__Theme')) {
                return;
              }
              handleThemeChange({
                themeId: theme.id,
                checked: !theme.checked
              });
              handleSearchDisable(themesToShow, theme.id, !theme.checked);
            },
            children: _jsx(Box, {
              px: 3,
              children: _jsx(ThemeRow, {
                theme: theme,
                search: search,
                responseTextId: responseTextId
              })
            })
          }, `${theme.id}-${responseTextId}`))
        })
      }), _jsx(Menu.Group, {
        children: _jsx(AddThemeButton, {
          allThemes: allThemes,
          themesSelected: themesSelected,
          search: search,
          disabled: error.error,
          handleSaveThemes: handleSaveThemes,
          handleThemeCreationForResponse: handleThemeCreationForResponse
        })
      })]
    })
  });
};
export default ThemeMenu;